import { Container } from '@mui/system'
import { useAtomValue } from 'jotai'
import { useEffect, useState, type CSSProperties } from 'react'
import { Link } from 'react-router'
import IcBaselineAdd from '~icons/ic/baseline-add'
import IcRoundTranslate from '~icons/ic/round-translate'
import MingcuteSearchLine from '~icons/mingcute/search-line'
import { Slot } from 'src/components/Common/slot'
import { OrdinaryMembershipHeaderBannerStatic } from 'src/features/Membership/components/MembershipHeaderBannerStatic'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from 'src/i18n/constants'
import styles from 'src/layouts/AppLayout.module.css'
import { desktopNavigationExpandAtom } from 'src/stores/navigation'
import { cx } from 'src/utils/cx'
import { useOptionalRequestContext } from 'src/utils/request-context'
import { useLocation, useMatches } from 'src/utils/router/route'

function isPathWithin(pathname: string, basePath: string) {
  return pathname === basePath || pathname.startsWith(`${basePath}/`)
}

function stripLocalePrefix(pathname: string) {
  const locale = pathname.split('/').filter(Boolean)[0]
  if (locale in SUPPORTED_LOCALES) {
    const stripped = pathname.replace(new RegExp(`^/${locale}`), '')
    return stripped || '/'
  }
  return pathname
}

function resolveCurrentLocale(pathname: string) {
  const locale = pathname.split('/').filter(Boolean)[0]
  return locale in SUPPORTED_LOCALES ? locale : DEFAULT_LOCALE
}

function withLocalePrefix(locale: string, pathname: string) {
  return `/${locale}${pathname === '/' ? '' : pathname}`
}

function getMatchedHandleValue(
  matches: ReturnType<typeof useMatches>,
  handleKey: string,
) {
  const match = matches.findLast(({ handle }) => {
    return (
      handle &&
      typeof handle === 'object' &&
      handleKey in handle &&
      (handle as Record<string, unknown>)[handleKey] !== undefined
    )
  })

  if (!match?.handle || typeof match.handle !== 'object') return undefined
  return (match.handle as Record<string, unknown>)[handleKey]
}

function shouldShowSearchBar(pathname: string) {
  return (
    pathname === '/' ||
    isPathWithin(pathname, '/market') ||
    isPathWithin(pathname, '/search')
  )
}

function classifyContainer(pathname: string) {
  const isArtworkPage =
    isPathWithin(pathname, '/artwork') || isPathWithin(pathname, '/video')

  const isLargeMaxWidthPage =
    pathname === '/' ||
    pathname === '/ranking' ||
    isPathWithin(pathname, '/search') ||
    isPathWithin(pathname, '/market') ||
    isPathWithin(pathname, '/generator/preset')

  const isFullWidthPage =
    pathname === '/train-lora' ||
    pathname === '/user-rank' ||
    pathname === '/eap' ||
    pathname === '/membership/plans' ||
    pathname === '/membership/credit-packs' ||
    pathname === '/user-referral' ||
    isPathWithin(pathname, '/referral') ||
    isPathWithin(pathname, '/world') ||
    isPathWithin(pathname, '/event')

  return { isArtworkPage, isLargeMaxWidthPage, isFullWidthPage }
}

function getContainerClassName(pathname: string, showingAppNav: boolean) {
  const { isArtworkPage, isLargeMaxWidthPage, isFullWidthPage } =
    classifyContainer(pathname)

  // NOTE: Keep the `mmd:pl-3 mmd:pr-1` base alive on large-max-width pages
  // (home / ranking / search / market / generator/preset). The CSR AppLayout
  // applies the equivalent `max-w-[108rem] + px-0` via MUI `classes.root` and
  // lets the `mmd:pl-3 mmd:pr-1` from the outer className win at ≥900px so
  // that `<aside>` (96/208px) has a 0.75rem gap before the artwork grid.
  // Using `mmd:!px-0` here — as the shell previously did — nukes that gap
  // and makes the artwork grid butt directly against the app nav rail.
  return cx(
    'min-w-0 mmd:pl-3 mmd:pr-1',
    !showingAppNav && 'col-span-full',
    isArtworkPage && 'mx-auto w-full max-w-[108rem] !px-0 md:px-6 mmd:!px-0',
    !isArtworkPage &&
      isLargeMaxWidthPage &&
      'mx-auto w-full max-w-[108rem] px-2 md:px-6',
    !isArtworkPage && !isLargeMaxWidthPage && isFullWidthPage && 'w-full !px-0',
    !isArtworkPage && !isLargeMaxWidthPage && !isFullWidthPage && 'w-full',
  )
}

/**
 * Mirror the CSR `AppLayout`'s Container `maxWidth` resolution:
 *
 * - Pages that bring their own max-width (artwork / large-max-width) or
 *   that want to fill the viewport (train-lora / membership / world /
 *   event / …) pass `maxWidth={false}` so MUI's default
 *   `maxWidth="lg"` (1200px) doesn't clamp them.
 * - Every other page (model / tag / user profile / news / …) omits the
 *   prop so MUI Container applies its default `lg` max-width and
 *   centers the content — matching the centered layout visible on
 *   production for `/model/:id`.
 */
function getContainerMaxWidth(pathname: string): false | undefined {
  const { isArtworkPage, isLargeMaxWidthPage, isFullWidthPage } =
    classifyContainer(pathname)
  if (isArtworkPage || isLargeMaxWidthPage || isFullWidthPage) return false
  return undefined
}

/**
 * SSR-safe navigation skeleton for the _app layout.
 *
 * Outputs semantic HTML (header/nav/main/footer) that is visible in View Source.
 * The full interactive AppLayout (search, notifications, account menu, etc.)
 * hydrates into dedicated slots on the client side.
 *
 * Responsive layout uses CSS media queries only — no JS hooks like useIsMobile.
 *
 * Track 0A intentionally keeps this shell free of client i18n runtime hooks.
 * Locale-aware links preserve the current prefix; the language control remains
 * a static anonymous affordance until Track 0B.
 */
export function AppFrameShell({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const matches = useMatches()
  const requestContext = useOptionalRequestContext()
  const showExpandedAppNav = useAtomValue(desktopNavigationExpandAtom)
  const normalizedPathname = stripLocalePrefix(location.pathname)
  const showAppNavFromRoute = Boolean(
    getMatchedHandleValue(matches, 'showAppNav'),
  )
  const showingTopBanner = !getMatchedHandleValue(
    matches,
    'hideMembershipBanner',
  )
  const showingAppFooter = !getMatchedHandleValue(matches, 'hideAppFooter')
  const showingAppNav =
    showAppNavFromRoute && !normalizedPathname.startsWith('/@')
  const currentLocale = resolveCurrentLocale(location.pathname)
  const showGenerateButton = normalizedPathname !== '/'
  const showDesktopSearchBar = shouldShowSearchBar(normalizedPathname)
  const mayTokenBeValid = requestContext?.mayTokenBeValid ?? false
  const [bannerContainerEl, setBannerContainerEl] =
    useState<HTMLDivElement | null>(null)
  // SSR-safe initial header height. Must equal whatever the
  // `useEffect` below settles on for the first commit, otherwise
  // `--app-header-height` shifts on hydrate and any
  // `calc(100svh - var(--app-header-height) ...)` consumer (most
  // visibly the artwork-detail hero `max-height`) jumps. The CSR
  // chrome on `>=mmd` is `64px header + 32px banner` when the
  // membership/promo banner is enabled, and just `64px` when it's
  // hidden via the `hideMembershipBanner` route handle. Mobile
  // (`<mmd`) ends up at `48 + 32 = 80px` (or `48px` no-banner) but
  // we deliberately bias the SSR fallback to the desktop value:
  //   - artwork / model detail are SEO routes whose primary
  //     audience is desktop search;
  //   - over-reserving 16px on mobile shrinks the hero downward by
  //     16px on hydrate, which is far less perceptible than the
  //     32px upward jump caused by under-reserving (the previous
  //     `64` default).
  const SSR_BANNER_HEIGHT = showingTopBanner ? 32 : 0
  const SSR_HEADER_HEIGHT = 64 + SSR_BANNER_HEIGHT
  const [headerHeight, setHeaderHeight] = useState(SSR_HEADER_HEIGHT)
  const [hasHydrated, setHasHydrated] = useState(false)

  // Keep the SSR shell deterministic on the server, then follow the persisted
  // desktop nav preference after hydration so expand/collapse can drive layout width.
  const effectiveShowExpandedAppNav =
    showingAppNav && (hasHydrated ? showExpandedAppNav : true)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQueryList = window.matchMedia('(min-width: 768px)')
    const updateHeaderHeight = () => {
      const baseHeaderHeight = mediaQueryList.matches ? 64 : 48
      const bannerHeight = bannerContainerEl?.offsetHeight ?? 0
      const next = baseHeaderHeight + bannerHeight
      setHeaderHeight(next)
      // Mirror the CSR `AppLayout` behaviour: also propagate the header
      // height to `<html>` so descendants rendered outside the shell
      // tree (e.g. MUI Dialog portals, ToastContainer) still see the
      // variable. Without this, any `calc(100vh - var(--app-header-height)
      // ...)` referenced from a portalled component evaluates to
      // `invalid` and its declaration collapses — most visibly on the
      // Model detail dialog, whose cover slider height goes to 0 and
      // hides the model cover image entirely.
      document.documentElement.style.setProperty(
        '--app-header-height',
        `${next}px`,
      )
    }

    updateHeaderHeight()

    const resizeObserver =
      bannerContainerEl && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateHeaderHeight)
        : null
    if (resizeObserver && bannerContainerEl) {
      resizeObserver.observe(bannerContainerEl)
    }
    mediaQueryList.addEventListener('change', updateHeaderHeight)

    return () => {
      resizeObserver?.disconnect()
      mediaQueryList.removeEventListener('change', updateHeaderHeight)
    }
  }, [bannerContainerEl, showingTopBanner])

  useEffect(() => {
    if (typeof document === 'undefined') return
    // Keep the other layout vars in sync with the root element so that
    // portalled components (Dialogs, Toasts, Photoswipe, …) can also
    // rely on them being defined. The shell-local inline style on the
    // outer div is still useful for SSR first paint, but anything
    // rendered into `document.body` via a React portal doesn't inherit
    // from that div.
    document.documentElement.style.setProperty(
      '--app-bottom-navigation-height',
      '0px',
    )
    document.documentElement.style.setProperty(
      '--open-in-app-banner-height',
      '0px',
    )
  }, [])

  useEffect(() => {
    setHasHydrated(true)
  }, [])

  return (
    <div
      className="flex flex-col min-h-screen"
      data-track-0a="app-frame-shell"
      data-auth-hint={mayTokenBeValid ? 'maybe-authenticated' : 'anonymous'}
      style={
        {
          '--app-header-height': `${headerHeight}px`,
          // `AppLayout`'s useEffect assigns these on the client. In the
          // SSR shell they're never set, which makes every
          // `calc(... - var(--app-bottom-navigation-height) ...)`
          // expression invalid and collapses the containing declaration
          // back to its initial value (observed on artwork detail
          // `max-height`, which silently became `none` on first paint).
          // Declare inert defaults here so SSR and CSR evaluate the same
          // `calc()` and the CSR useEffect can still overwrite them.
          '--app-bottom-navigation-height': '0px',
          '--open-in-app-banner-height': '0px',
        } as CSSProperties
      }
    >
      {showingAppNav ? (
        <Slot
          name="app-frame-open-app-banner"
          forceMount
          className="contents"
          data-track-0a="app-frame-open-app-banner"
        />
      ) : null}

      <div className="sticky top-0 w-full z-20">
        {showingTopBanner ? (
          // Reserve the vertical space of the top promo banner even before
          // RootRuntimeInner mounts AppTopBannerSlot into this slot.
          // Anonymous/new-user visits on SSR-enabled routes almost always
          // hydrate into a 32px gradient banner ("PixAI Membership" / promo /
          // Upgrade Plan), so we render a fully static "anonymous default"
          // facsimile of `OrdinaryMembershipHeaderBanner` here. The live
          // banner (`AppTopBannerSlot` -> `useBannerState` ->
          // `OrdinaryMembershipHeaderBanner` / `NewUserDiscountBanner` /
          // `MonthlyDiscountBanner`) is mounted by `RootRuntimeInner` once
          // Apollo / breakpoint hooks are available; the slot's portal then
          // takes over and replaces the static placeholder with the real
          // banner. The DOM shape (32px gradient row + crown + label +
          // chevron + link) is identical, so the swap is silent even when
          // the live state is something other than the anonymous default.
          <div
            ref={setBannerContainerEl}
            className="min-h-8"
            data-track-0a="app-frame-top-banner-slot"
          >
            <Slot
              name="app-frame-top-banner"
              forceMount
              className="block"
              data-track-0a="app-frame-top-banner"
            >
              <OrdinaryMembershipHeaderBannerStatic />
            </Slot>
          </div>
        ) : null}

        <header
          role="banner"
          className="flex items-center bg-background/95 backdrop-blur-md px-3 gap-2 h-12 mmd:px-4 mmd:gap-4 mmd:h-16"
        >
          <Link
            to={withLocalePrefix(currentLocale, '/')}
            className="group flex-none inline-flex items-center py-3 no-underline mmd:px-3"
            suppressHydrationWarning
          >
            <div className="size-7 mmd:size-9">
              <Slot name="app-frame-logo" forceMount className="contents">
                <img
                  src="/favicon.anniversary.svg"
                  alt="PixAI"
                  className="size-full"
                  width={36}
                  height={36}
                />
              </Slot>
            </div>
            <Slot name="app-frame-logo-title" forceMount className="contents">
              {/* Fallback markup is kept visually identical to the
                  CSR enhancement output (`<CustomHeroTitleWithDescription
                  title="PixAI" size="small" className="ml-2 !mt-0
                  max-mmd:hidden" />`) so the brand wordmark does not
                  flip from a 14px `font-semibold` span to a 24px
                  `font-pixai-rounded` heading on hydrate. All locales
                  resolve `common:artwork-list.hero.title` to the
                  literal string "PixAI", so a static literal here
                  cannot drift from the hydrated copy. */}
              <div className="ml-2 py-2 text-center max-mmd:hidden">
                <div className="font-pixai-rounded relative msm:text-2xl">
                  PixAI
                </div>
              </div>
            </Slot>
          </Link>

          <Slot
            name="app-frame-header-mobile-discord"
            forceMount
            className="contents"
          >
            <div
              className="mmd:hidden inline-flex items-center justify-center size-8 rounded-full bg-background-secondary border border-black/10 dark:border-white/10"
              aria-hidden
            />
          </Slot>

          <span className="ml-auto" />

          <Slot
            name="app-frame-header-credit-pill"
            forceMount
            className="contents"
          />

          <Slot
            name="app-frame-header-generate"
            forceMount
            className="hidden mmd:contents"
          >
            {showGenerateButton ? (
              <Link
                to={withLocalePrefix(currentLocale, '/generator/image')}
                className="inline-flex items-center px-4 py-2 font-medium no-underline bg-theme-primary hover:bg-violet-500 dark:bg-violet-600 text-white rounded-full h-10 gap-0.5"
                suppressHydrationWarning
              >
                <IcBaselineAdd className="-ml-[6px] size-6" />
                <span className="leading-none">Generate</span>
              </Link>
            ) : null}
          </Slot>

          <Slot
            name="app-frame-header-search"
            forceMount
            className="hidden mmd:contents"
          >
            {showDesktopSearchBar ? (
              <Link
                to={withLocalePrefix(currentLocale, '/search')}
                className="inline-flex items-center h-10 min-w-56 rounded-full border border-black/10 dark:border-white/10 bg-background-secondary px-4 no-underline"
                aria-label="Search"
                suppressHydrationWarning
              >
                <MingcuteSearchLine className="size-5 flex-none text-primary" />
                <span
                  className="ml-2 block h-3 w-24 rounded-full bg-black/10 dark:bg-white/10"
                  aria-hidden
                />
              </Link>
            ) : null}
          </Slot>

          <Link
            to={withLocalePrefix(currentLocale, '/search')}
            className="mmd:hidden inline-flex items-center justify-center size-8 rounded-full bg-background-secondary border border-solid border-black/50 dark:border-white/50"
            aria-label="Search"
            suppressHydrationWarning
          >
            <MingcuteSearchLine className="size-5 text-primary" />
          </Link>

          {/*
            Keep the Slot's own flex gap in sync with the outer `<header>`
            (`px-3 gap-2` on mobile, `mmd:px-4 mmd:gap-4` on desktop). In the
            production CSR layout (`AppLayout.tsx`), NotificationButton /
            SignButton / AccountMenu live directly under the header flex and
            inherit the 16px desktop gap, but here they're wrapped in this
            Slot so we need to mirror that gap explicitly — otherwise the
            "Sign in" button butts too close to the language pill.
          */}
          <Slot
            name="app-frame-header-controls"
            className="flex items-center gap-2 mmd:gap-4"
          >
            <div
              className="shrink-0 rounded-full size-8 mmd:size-10 bg-background-secondary border border-black/10 dark:border-white/10"
              aria-hidden
            />

            {mayTokenBeValid ? (
              <div
                className="shrink-0 rounded-full size-8 mmd:size-10 bg-background-secondary border border-black/10 dark:border-white/10 mr-2"
                aria-label="Account"
              />
            ) : (
              // Match the hydrated CSR button geometry exactly:
              //   <SignButton> → <UIButton size="large" variant="outlined">
              //   which resolves to h-10 / pl-3 pr-3 / text-base on desktop.
              // The previous shell used `mmd:px-4` and `text-sm`, which made
              // the button ~6-8px wider before hydrate and shifted the
              // language pill to the right on hydration.
              <Link
                to={withLocalePrefix(currentLocale, '/login')}
                className="inline-flex items-center bg-background border border-theme-primary hover:bg-violet-100 dark:hover:bg-slate-800 text-theme-primary no-underline whitespace-nowrap rounded-full px-3 h-8 text-sm mmd:h-10 mmd:text-base"
                suppressHydrationWarning
              >
                Sign in
              </Link>
            )}

            {/*
              `-mr-1` mirrors the `-mr-1` applied to `<AccountButton>` in
              `AppFrameHeaderControlsEnhancement`; without it the CSR version
              would pull the language pill 4px further right once it mounts.
            */}
            <div
              aria-label="Language"
              title={currentLocale.toUpperCase()}
              className="inline-flex items-center justify-center size-8 mmd:size-10 rounded-full bg-background-secondary border border-black/50 dark:border-white/50 -mr-1"
            >
              <IcRoundTranslate className="size-5 mmd:size-6 text-primary" />
            </div>
          </Slot>
        </header>
      </div>

      <div
        className={cx(
          'flex-1 transition-all duration-300',
          // Only apply the 3-column grid (`nav | main | gutter`) when the
          // route actually opts into the desktop side nav. Otherwise (e.g.
          // `/@username/*` profile pages that set `handle.showAppNav = false`)
          // <main> has no `gridArea` and would be auto-placed into the first
          // track (nav column, ~96px), squeezing the whole page into a narrow
          // strip on desktop. Leaving the wrapper as a plain flex container
          // lets <main> consume the full width as intended.
          showingAppNav &&
            (effectiveShowExpandedAppNav
              ? styles['app-layout-expanded']
              : styles['app-layout']),
        )}
      >
        {showingAppNav && (
          <Slot
            name="app-frame-desktop-navigation"
            forceMount
            className="hidden mmd:contents"
            data-track-0a="app-frame-aside"
          >
            <aside className="hidden mmd:flex flex-col justify-start w-[--app-nav-width] shadow-[1px_0px_#0002] dark:shadow-[inset_-1px_0px_#fff2] relative transition-[width]">
              <div className="sticky top-[calc(var(--app-header-height)+1em)] w-[calc(100%+0.75em)] h-0 overflow-visible flex justify-end z-100 transition-all">
                <div
                  className="rounded-full bg-background size-6 shadow-lg dark:ring-2 dark:ring-neutral-500"
                  aria-hidden
                />
              </div>

              <nav
                aria-label="App Navigation"
                className="sticky top-[--app-header-height] flex flex-col py-1"
                suppressHydrationWarning
              >
                {Array.from({ length: 5 }).map((_, index) => (
                  <div
                    key={index}
                    className="mx-2 my-1 inline-flex items-center justify-center size-12 rounded-2xl bg-background-secondary/60"
                    aria-hidden
                  />
                ))}
              </nav>

              <span className="mt-auto" />

              <div className="sticky bottom-0 bg-background py-1">
                <div
                  className="mx-2 my-1 inline-flex items-center justify-center size-12 rounded-2xl bg-background-secondary/60"
                  aria-hidden
                />
              </div>
            </aside>
          </Slot>
        )}

        <main
          data-track-0a="app-frame-main"
          className="min-w-0"
          style={{ gridArea: showingAppNav ? 'main' : undefined }}
        >
          <Container
            maxWidth={getContainerMaxWidth(normalizedPathname)}
            className={getContainerClassName(normalizedPathname, showingAppNav)}
          >
            {children}
          </Container>
        </main>
      </div>

      {showingAppNav ? (
        <Slot
          name="app-frame-mobile-navigation"
          forceMount
          className="contents"
          data-track-0a="app-frame-mobile-navigation"
        />
      ) : null}

      {showingAppFooter ? (
        <Slot name="app-frame-footer" forceMount className="mt-auto">
          <footer
            data-track-0a="app-frame-footer"
            className="px-4 msm:px-6 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] text-sm text-primary-light bg-background border-t border-black/10 dark:border-white/10 max-mmd:mb-[60px]"
          >
            <div className="w-full max-w-screen-2xl mx-auto flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <img
                  src="/favicon.anniversary.svg"
                  alt=""
                  aria-hidden
                  className="size-6"
                  width={24}
                  height={24}
                />
                <span className="text-primary-lighter">
                  &copy; {new Date().getFullYear()} PixAI
                </span>
              </div>
            </div>
          </footer>
        </Slot>
      ) : null}
    </div>
  )
}
